<template>
  <div id="createTemplatePage" :class="['page white', {'template-grabbing': isTemplateGrabbing}]" refs="createTemplatePage">
    <el-container class="content-aside workspace">
      <!-- Left Side -->
      <el-main class="cpl-main workspace">
        <div class="options workspace">
          <el-row :gutter="20" class="workspace">
            <!-- Page Content Options -->
            <el-col class="content-options-col create-main-options workspace"> </el-col>
            <el-col class="search-col workspace"></el-col></el-row>
        </div>

        <!-- Main Content -->
        <div :style="{ display: 'flex', height: '100%' }">
          <div v-if="noStylePackAccess">
            <BaseEmptyState
              class="text-left! items-start!"
              :title="$t('templates.noTemplatesAvailable')"
              data-component-element-id="no-templates-access-placeholder"
            >
              <template #default>
                <div class="text-sm text-gray-600">{{ $t('templates.noStylePackAccess') }}</div>
              </template>
            </BaseEmptyState>
          </div>
          <template v-else>
            <!-- Template Orientation -->
            <div v-if="isHorizontalAndVerticalIconVisible" class="orientation-options workspace">
              <div class="items">
                <el-tooltip :popper-class="'custom-tooltip-popper-filters'" :show-arrow="false" effect="light" class="item" :content="$t('label.horizontalView')" placement="bottom">
                  <div @click="handleProjectOrientationClick('horizontal')"
                    :class="{'item': true, 'horizontal': true, 'is-active': (projectOrientation === 'horizontal')}">
                  </div>
                </el-tooltip>

                <el-tooltip :popper-class="'custom-tooltip-popper-filters'" :show-arrow="false" effect="light" class="item" :content="$t('label.verticalView')" placement="bottom">
                  <div @click="handleProjectOrientationClick('vertical')"
                    :class="{'item': true, 'vertical': true, 'is-active': (projectOrientation === 'vertical')}">
                  </div>
                </el-tooltip>
              </div>
            </div>

            <!-- Template Card -->
            <div
              id="templateListContainer"
              class="cards-row project-frame-content workspace hide-scrollbar">
              <draggable
                v-bind="sortDraggableOptions"
                v-model="template.templates"
                id="templateSetsPageSortWrapper"
                class="workspace flex flex-wrap"
                tag="span"
                :item-key="(item) => item?.order"
                :component-data="{name:'flip-list'}"
                :disabled="!enableEditTemplateSet"
                :forceFallback="true"
                filter=".el-input__inner"
                :preventOnFilter="false"
                fallbackClass="sortable-fallback-template-card-cloned"
                fallbackOnBody="true"
                ghostClass="sortable-ghost"
                delay="0"
                :clone="handleTemplateSortClone"
                @start="handleTemplateSortStart"
                @end="handleTemplateSortEnd">

                <template #item="{ element: item, index: indx }">
                  <el-col
                    :span="projectOrientation === 'vertical' ? 6 : 8"
                    :id="`templateID${item.templateId}`"
                    :class="['template-item', `template-item-${item.templateId}`, `${item._edit.cardSizeClass}`, {'vertical': (projectOrientation === 'vertical')}]">
                    <div :class="['content content-template-item', {'vertical': (projectOrientation === 'vertical'), 'disabled': !enableCreateNewProject}]">
                      <div :class="['template-preview-fixed', {'firefox': $utils.getBrowser() === 'Firefox'}]">
                        <!-- Project Preview -->
                        <template v-if="item.preview.type && item.preview.type === 'svg'">
                          <iframe :id="`template-${item.templateId}`"
                            :ref="item.templateId"
                            :src="$utils.filterMediaPreviewUrl(item.preview.url)"
                            :data-src="$utils.trimDataSrc(item.preview.url)"
                            @load="handleSVGOnLoad()"
                            allow="clipboard-read *; clipboard-write *;"
                            :class="['object-fit_cover_center template-target-on-refresh-token project-template', `media-preview-${item._edit.uid}`]">
                          </iframe>
                        </template>
                        <div class="no-preview" v-if="item.preview.type === undefined">
                          <div class="desc">
                            <el-icon class="el-icon-warning"><WarningFilled /></el-icon>
                            <p> {{$t('label.previewUnavailable')}} </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div @click.capture="handleTemplateCardCoverClickCapture(item, $event)"
                      @mouseover="handleTemplateCardMouseOver(item)"
                      @mouseout="handleTemplateCardMouseOut(item)"
                      :class="['preview-content-cover']">
                    </div>

                    <div :class="['name', {'disabled-edit-name': !enableEditTemplateSet || (templateSetsSelected && !templateSetsSelected.userDefined) }]">
                      <!-- Name Txt -->
                      <div v-if="(!inlineEditName.isInputVisible && inlineEditName.templateId === item.templateId) ||
                        (inlineEditName.templateId !== item.templateId)"
                        @click="handleInlineEditNameClick($event, item, indx)"
                        :class="['template-item-name', {'has-status': (item.new || item.updated || item.modifiedFile)}]">
                        <span v-if="item.new" class="status">{{$t('label.new')}}</span>
                        <span v-if="item.updated" class="status">{{$t('label.updated')}}</span>
                        <span v-if="item._edit && item._edit.modifiedFile" class="status">{{$t('label.new')}}</span>{{item.name}}</div>

                      <!-- Edit Name Input -->
                      <el-input
                        v-if="inlineEditName.isInputVisible && inlineEditName.templateId === item.templateId"
                        class="edit-template-item-name"
                        id="template-item-name-input"
                        :model-value="item.name"
                        @update:model-value="newValue => item.name = newValue"
                        :ref="`${item.templateId}-editName`"
                        size="mini"
                        @blur="handleInlineEditNameInputConfirm(item, 'onBlur')"
                        @focus="$event.target.select()">
                      </el-input>
                    </div>

                    <div class="card-actions">
                      <div v-if="enableEditTemplateSet" @click.capture="handleHideTemplateInSet(item)" @mouseover="handleCardHoverStyle(item.templateId, 'actionRemoveIconHover')" class="icon remove"></div>
                      <div v-if="enableEditTemplateSet" @click.capture="handleCopyTemplateInSet(item)" @mouseover="handleCardHoverStyle(item.templateId, 'actionCopyIconHover')" class="icon copy"></div>
                    </div>
                  </el-col>
                </template>
              </draggable>
            </div>
          </template>
        </div>
      </el-main>

      <el-aside class="cpl-aside workspace">
        <div :class="['content workspace hide-scrollbar', {'disabled-new-set-button': (!enableNewTemplateSetButton)}]">
          <template v-if="enableNewTemplateSetButton">
            <template v-if="defaultTemplateSet && isCreateStyleEnabled">
              <el-tooltip :popper-class="'custom-tooltip-popper-filters capitalize-text'" :show-arrow="false" effect="light" class="item" :content="$t('label.createStyle')" placement="top">
                <el-button
                  :class="{
                    'cstm-icon-btn-medium style-action-btn white-background create-style mr-[5px]!': true,
                    'is-style-menu-enabled': (['newTemplateSet', 'editTemplateSet'].indexOf(templateMenuType) >= 0),
                    'disabled': !enableChangeTemplateSetSettings
                  }"
                  @click="handleTemplateSetTagClick(defaultTemplateSet, 'create', true)" :disabled="!enableChangeTemplateSetSettings">
                </el-button>
              </el-tooltip>
            </template>
            <el-tooltip :popper-class="'custom-tooltip-popper-filters capitalize-text'" :show-arrow="false" effect="light" class="item" :content="$t('label.savedStyles')" placement="top">
              <el-button :class="['cstm-icon-btn-medium', 'style-action-btn', 'saved-styles', {'is-style-menu-enabled': templateMenuType === null}]" @click="handleStyleActionBtnClick">
              </el-button>
            </el-tooltip>
          </template>

          <!--Template Set Menu-->
          <template-set-properties v-if="['newTemplateSet', 'editTemplateSet'].indexOf(templateMenuType) >= 0"
            :templateProp="template"
            :tagsSummaryProp="tagsSummary"
            :templateMenuTypeProp="templateMenuType"
            :countTemplateSetsProp="templateSets.length"
            :maxCountTemplateSetsProp="4"
            @properties-change="handleTemplatePropertiesChange($event)">
          </template-set-properties>

          <!-- Template Set List -->
          <perfect-scrollbar v-if="templateMenuType === null" class="template-sets workspace white-background cstm-ps-scrollbar-vertical">
            <p class="template-sets-label">{{ $t('label.savedStyles') }}</p>
            <div>
              <el-input class="search-input" :placeholder="$t('label.search')" v-model="searchValue"></el-input>
            </div>
            <div v-for="(templateSet, indx) in filteredTemplateSets" :key="indx" class="set">
              <!--Edit Template Set-->
              <template v-if="(templateSet.groupId !== 'default') || (templateSet.userDefined)">
                <div :class="['template-set-title', 'details', `styleId${templateSet.templateSetId}`, {'is-selected': templateSet._edit.isSelected, 'copy-template-set-disabled': !enableEditTemplateSet}]"
                  @mouseover="handleTemplateSetMouseMoveEvents('mouseover', templateSet)"
                  @mouseout="handleTemplateSetMouseMoveEvents('mouseout', templateSet)">
                  <el-button @click="handleTemplateSetTagClick(templateSet, 'edit', true)"
                    :class="['white-background', {'is-selected': templateSet._edit.isSelected}]">
                    <img :src="templateSet.options.logo.preview.url" alt="logo">
                    <span class="template-set-name">{{templateSet.name}}</span>
                  </el-button>
                  <div :class="['style-set-droppable']"></div>

                  <el-tooltip v-if="enableChangeTemplateSetSettings" :content="$t('label.delete')" effect="light" placement="bottom" popper-class="custom-tooltip-popper" :show-arrow="false">
                    <div @click="handleDeleteTemplateSet(templateSet)" class="template-set-action-icon delete">
                    </div>
                  </el-tooltip>
                  <el-tooltip v-if="enableEditTemplateSet" :content="$t('label.duplicate')" effect="light" placement="bottom" popper-class="custom-tooltip-popper" :show-arrow="false">
                    <div @click="handleTemplateSetCopy(templateSet)" class="template-set-action-icon copy">
                    </div>
                  </el-tooltip>
                  <div class="template-set-palette">
                    <span :style="{backgroundColor: templateSet.options.logoColor}"></span>
                    <span :style="{backgroundColor: templateSet.options.foregroundColor}"></span>
                    <span :style="{backgroundColor: templateSet.options.backgroundColor}"></span>
                    <span :style="{backgroundColor: templateSet.options.textColor}"></span>
                    <span :style="{backgroundColor: templateSet.options.subtextColor}"></span>
                  </div>
              </div>
              </template>
            </div>
          </perfect-scrollbar>
        </div>
      </el-aside>
    </el-container>
  </div>
</template>

<script src="./create-template-set.js"></script>
<style lang="scss" src="./create-template-set.scss"></style>
